// Shared backtest-run input.
//
// Backtest and Replay used to be two INDEPENDENT runs of the same engine on
// independently-chosen data + a hardcoded Replay balance → their trades only
// coincidentally matched. This context publishes the EXACT inputs of the
// last executed backtest (the prepared blocks, the precise market series,
// the initial balance) so Replay steps through *that* run:
//
//   same blocks + same series + same balance + same engine
//     ⇒ Replay trades are IDENTICAL to Backtest trades, by construction.
//
// In-memory only: Replay is used in the same session right after Backtest;
// not persisting avoids showing a stale cross-session run and keeps scope
// minimal. StrategyContext still owns the live canvas blocks; this snapshots
// the blocks AS EXECUTED so a later canvas edit can't desync the replay.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Block, MarketDataPoint } from '../types';

export interface BacktestRunInput {
  blocks: Block[];                 // EXACT preparedBlocks the engine ran on
  marketData: MarketDataPoint[];   // EXACT series the engine ran on
  initialBalance: number;
  symbol: string;
  exchange: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  marketId: string;
  ranAt: number;                   // wall-clock of the run (display only)
}

interface MarketDataContextValue {
  runInput: BacktestRunInput | null;
  publishRunInput: (r: BacktestRunInput) => void;
  clearRunInput: () => void;
}

const MarketDataContext = createContext<MarketDataContextValue>({
  runInput: null,
  publishRunInput: () => {},
  clearRunInput: () => {},
});

export const MarketDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [runInput, setRunInput] = useState<BacktestRunInput | null>(null);
  const publishRunInput = useCallback((r: BacktestRunInput) => setRunInput(r), []);
  const clearRunInput = useCallback(() => setRunInput(null), []);
  const value = useMemo<MarketDataContextValue>(
    () => ({ runInput, publishRunInput, clearRunInput }),
    [runInput, publishRunInput, clearRunInput],
  );
  return <MarketDataContext.Provider value={value}>{children}</MarketDataContext.Provider>;
};

export function useMarketRun(): MarketDataContextValue {
  return useContext(MarketDataContext);
}
